import React from "react";

import type { TSession } from "../../../../../utils/types";
import { getPatients } from "../../../../../services/api-clinic";
import type { TPatientOption } from "../types";
import { toPatientOption } from "../utils/parsers";

type UseDebouncedPatientSearchParams = {
  session: TSession | null | undefined;
  searchTerm: string;
  enabled?: boolean;
  debounceMs?: number;
  limit?: number;
  onResults?: (results: TPatientOption[]) => void;
};

type UseDebouncedPatientSearchReturn = {
  results: TPatientOption[];
  loading: boolean;
  error: string | null;
};

export const useDebouncedPatientSearch = ({
  session,
  searchTerm,
  enabled = true,
  debounceMs = 300,
  limit = 20,
  onResults,
}: UseDebouncedPatientSearchParams): UseDebouncedPatientSearchReturn => {
  const [results, setResults] = React.useState<TPatientOption[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!enabled || !session) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    const normalizedSearchTerm = searchTerm.trim();
    if (!normalizedSearchTerm || normalizedSearchTerm.length === 1) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    let isMounted = true;
    const timeoutId = setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        const nextResults = await getPatients(session, {
          search: normalizedSearchTerm,
          limit,
        });

        if (!isMounted) {
          return;
        }

        const nextPatientOptions = (nextResults || []).map(toPatientOption);
        setResults(nextPatientOptions);
        onResults?.(nextPatientOptions);
      } catch (searchError) {
        if (!isMounted) {
          return;
        }

        setResults([]);
        setError("Unable to search patients right now.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [debounceMs, enabled, limit, onResults, searchTerm, session]);

  return {
    results,
    loading,
    error,
  };
};
